import { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Floating "back to top" button — appears after the user scrolls past 800px
 * down. Smooth-scroll to the top of the page on click, instant on
 * reduced-motion. Anchored bottom-right desktop, above BottomNavMobile on
 * mobile.
 *
 * @example
 *   <BackToTop />  // mounted globally in <Layout>
 */
export default function BackToTop() {
  const [visible, setVisible] = useState(false);
  const reduce = useReducedMotion();
  const { t } = useTranslation('components'); // aria-label i18n (audit 2026-07-08)

  useEffect(() => {
    let frame = 0;
    const update = () => setVisible(window.scrollY > 800);
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const handleClick = () => {
    // Route via Lenis quand il est actif (scroll cohérent, pas de concurrence
    // entre le smooth natif et l'inertie Lenis). Repli natif sinon.
    if (window.__lenis && !reduce) {
      window.__lenis.scrollTo(0);
    } else {
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          type="button"
          onClick={handleClick}
          aria-label={t('a11y.backToTop')}
          initial={reduce ? { opacity: 0 } : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, y: 16 }}
          transition={{ duration: 0.2 }}
          // Bottom anchor : 1.25rem desktop, 4.5rem mobile (above BottomNavMobile bar).
          className="fixed bottom-[4.5rem] right-4 z-30 inline-flex h-11 w-11 items-center justify-center rounded-full bg-navy-900 text-cream-50 shadow-card-hover transition-transform hover:bg-bordeaux-700 hover:scale-105 focus-visible:bg-bordeaux-700 lg:bottom-5 lg:right-5 print-hide dark:bg-white dark:text-black dark:shadow-none dark:hover:bg-bordeaux-500 dark:hover:text-white dark:focus-visible:bg-bordeaux-500 dark:focus-visible:text-white"
          data-print="hide"
        >
          <ArrowUp size={18} aria-hidden="true" />
        </motion.button>
      )}
    </AnimatePresence>
  );
}
